import { encode, decode } from 'blurhash';

// BlurHash encoding configuration
const BLURHASH_COMPONENT_X = 4;
const BLURHASH_COMPONENT_Y = 3;
const THUMBNAIL_SIZE = 32; // Small size for fast encoding

/**
 * Generate a blurhash string from an image URL
 * Works with blob URLs from file input
 */
export async function generateBlurhash(imageSource: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        // Create small canvas for faster encoding
        const canvas = document.createElement('canvas');
        canvas.width = THUMBNAIL_SIZE;
        canvas.height = Math.round(THUMBNAIL_SIZE * (img.height / img.width)) || THUMBNAIL_SIZE;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const blurhash = encode(
          imageData.data,
          imageData.width,
          imageData.height,
          BLURHASH_COMPONENT_X,
          BLURHASH_COMPONENT_Y
        );

        resolve(blurhash);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image for blurhash generation'));
    img.src = imageSource;
  });
}

/**
 * Decode a blurhash string to a data URL for use as background image
 */
export function decodeBlurhashToDataURL(
  blurhash: string,
  width: number = 32,
  height: number = 32
): string {
  try {
    const pixels = decode(blurhash, width, height);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);

    return canvas.toDataURL();
  } catch (error) {
    console.error('Failed to decode blurhash:', error);
    return '';
  }
}

/**
 * Check if a string is a valid blurhash
 */
export function isValidBlurhash(blurhash: string | undefined | null): boolean {
  if (!blurhash || typeof blurhash !== 'string') return false;
  // Blurhash strings are typically 20-30 characters and contain alphanumeric + some symbols
  return blurhash.length >= 6 && /^[A-Za-z0-9#$%*+,-.:;=?@[\]^_{|}~]+$/.test(blurhash);
}
